/**
 * `clientDecisions` — `mockups/client/requests.html` §1–2, "Decisions waiting on
 * you", and the same panel on `client/dashboard.html` §3.
 *
 * `.docs/17-studio-read-models.md` §DRY watchlist: "The decision kinds' shapes are
 * one discriminated union in `packages/shared/schemas/readmodels/decisions.ts`, used
 * by the client dashboard, the requests page and the studio client detail." That
 * union already exists — `OpenDecisionSchema` in `./work-orders.js`, built in session
 * 11 and widened in 12 to the four kinds (`quote`, `extra`, `package_change`,
 * `content_plan`) it names today, and already the shape both
 * `work_orders.openDecisionsForClient` and `retainers.openDecisionsForClient` return.
 * Declaring a second union here would be the DRY violation this file exists to avoid
 * — so this file **re-uses** `OpenDecisionSchema` rather than restating it, and adds
 * only what is genuinely new: the read model's own envelope (the merged, ordered
 * list; the "decided in the last 60 days" fold; the hero's counts) and one derived
 * field, `late`, that no module stores.
 */
import { z } from 'zod';
import { DECISION_KINDS, OpenDecisionSchema } from '../work-orders.js';
import { MoneySchema, Rfc3339Schema } from '../primitives.js';
/**
 * One open decision, plus whether it is already late.
 *
 * `late` is true only for a `content_plan` sign-off whose first scheduled piece's day
 * has already passed (`.docs/17-studio-read-models.md` §Edge cases: "a content plan
 * past its first item date is flagged `late`") — the same fact
 * `ContentPlanSchema.late_signoff` records *after* a sign-off, read live for one that
 * has not happened yet. Every other kind is always `false`: a quote past its
 * `valid_until` is excluded from the list entirely
 * (`work_orders.queries.listOpenDecisions` already does that), not flagged.
 */
export const ClientDecisionItemSchema = OpenDecisionSchema.extend({
    late: z
        .boolean()
        .describe('True for a content-plan sign-off whose first scheduled piece is already due. Always ' +
        'false for the other three kinds, which are excluded rather than flagged once they ' +
        'expire.'),
}).describe('One decision waiting on the client, as `OpenDecisionSchema`, plus `late`.');
/** One decision from the "decided in the last 60 days" fold, and how it landed. */
export const ClientDecidedItemSchema = z
    .strictObject({
    kind: z.enum(DECISION_KINDS).describe('The same closed set `OpenDecisionSchema.kind` uses.'),
    ref: z.string().describe('What was decided, by reference.'),
    title: z.string().describe('What it was for.'),
    amount: MoneySchema.nullable().describe('What it committed to, or null for a sign-off.'),
    outcome: z.enum(['approved', 'declined']).describe('How it was decided.'),
    decided_at: Rfc3339Schema.describe('When.'),
})
    .describe('One decision already made, for the fold under the open ones.');
/**
 * `client/requests.html` §1's hero: "4", "Three of them carry money."
 *
 * `waiting` is `items.length`; `with_money` counts the ones whose `amount` is not
 * null. Both are read off `items` rather than recomputed, so the hero and the list
 * beneath it cannot disagree.
 */
export const ClientDecisionCountsSchema = z
    .strictObject({
    waiting: z.int().describe('Every open decision, of any kind.'),
    with_money: z.int().describe('The ones whose `amount` is not null.'),
})
    .describe('The hero figure and its one qualifying sentence.');
export const ClientDecisionsSchema = z
    .strictObject({
    items: z
        .array(ClientDecisionItemSchema)
        .describe('Every open decision, deadline-first: the ones with a `valid_until` sorted by it, ' +
        'then everything else oldest-proposed-first.'),
    decided_recently: z
        .array(ClientDecidedItemSchema)
        .describe('Approved or declined in the last 60 days, most recent first.'),
    counts: ClientDecisionCountsSchema,
})
    .describe('`client/requests.html` §1–2 and the dashboard’s Approvals panel: what is waiting on ' +
    'the client, and what they decided recently.');
//# sourceMappingURL=decisions.js.map